import { ContractInput, ContractInputLocation } from '../context/types';

// One legal-entity grouping inside a contract input. PR 2 of the multi-org
// migration: each distinct tax_id becomes one Contract at commit time, so the
// rest of the wizard (merchant resolution, customer-account, contract
// decision, fee-schedule materialization, CSPs) iterates over OrgGroups.
export interface OrgGroup {
  tax_id: string;
  // Locations belonging to this org, preserved in their original input order.
  locations: ContractInputLocation[];
  // First non-null medicare_number across the group's locations. Medicare
  // numbers are organization-level external identifiers; under our PR 1
  // single-name-per-tin assumption all locations in a group should agree, so
  // first-non-null is a faithful summary of the group's value.
  medicare_number?: string;
}

// Groups locations by tax_id, preserving first-appearance order. Single-TIN
// input returns a one-element array — every per-org wizard step uses the same
// iteration shape regardless of TIN count.
export const getOrgGroups = (input: ContractInput): OrgGroup[] => {
  const order: string[] = [];
  const byTin = new Map<string, ContractInputLocation[]>();
  for (const loc of input.locations) {
    const existing = byTin.get(loc.tax_id);
    if (existing == null) {
      order.push(loc.tax_id);
      byTin.set(loc.tax_id, [loc]);
    } else {
      existing.push(loc);
    }
  }
  return order.map(tin => {
    const locs = byTin.get(tin) ?? [];
    return {
      tax_id: tin,
      locations: locs,
      medicare_number: locs.find(
        l => l.medicare_number != null && l.medicare_number !== '',
      )?.medicare_number,
    };
  });
};

// Convenience: distinct tax_ids in input order. Used wherever a page only
// needs the keys (e.g. iterating over Record<tin, ...> wizard state).
export const distinctTins = (input: ContractInput): string[] =>
  getOrgGroups(input).map(g => g.tax_id);
